import React, {
    ReactElement,
    ChangeEvent,
    useState,
    useEffect,
    MouseEventHandler,
    ChangeEventHandler,
} from 'react';

import { IQATestableProps } from '../../shared/types/IQATestableProps';
import { ISetState } from '../../shared/types/ISetState';
import { IStyleableProps } from '../../shared/types/IStyleableProps';
import { ICON_TYPE, OzIcon } from '../icon/Icon';

import styles from './CheckBox.module.css';

export type ICheckBoxProps = IQATestableProps & IStyleableProps & {
    onChange?: ChangeEventHandler<HTMLInputElement>;
    onClick?: MouseEventHandler<HTMLInputElement>;
    disabled?: boolean;
    checked?: boolean;
    name?: string;
    id: string;
};

type ICheckedEvent = {
    target: {
        checked: boolean;
    };
};

export function OzCheckBox (props: ICheckBoxProps): ReactElement {
    const {
        disabled = false,
        'data-testid': dataTestId = '',
        className = '',
        onClick,
        onChange,
        name,
        checked: defaultChecked = false,
        id,
    }: ICheckBoxProps = props;

    const [checked, setChecked]: [boolean, ISetState<boolean>] = useState<boolean>(defaultChecked);

    useEffect((): void => {
        setChecked(defaultChecked);
    }, [defaultChecked]);

    function handleChange (event: ChangeEvent<HTMLInputElement>): void {
        const { target }: ICheckedEvent = event;

        setChecked(target.checked);

        if (onChange) {
            onChange(event);
        }
    }

    return (
        <div className="relative inline-block h-3 w-3">
            <input
                data-testid={dataTestId}
                type="checkbox"
                disabled={disabled}
                onChange={handleChange}
                onClick={onClick}
                className={
                    `cursor-pointer w-3 h-3 rounded border border-solid border-gray-300 appearance-none
                    checked:bg-teal-800 checked:border-teal-800 hover:border-gray-400 disabled:border-gray-200
                    disabled:cursor-not-allowed ${className}`
                }
                checked={checked}
                id={id}
                name={name}
            />
            <OzIcon
                type={ICON_TYPE.OZ_CHECK_MARK}
                className={
                    `absolute transform -translate-x-1/2 -translate-y-1/2 pointer-events-none top-50%
                    left-50% ${styles.checkmark}`
                }
            />
        </div>
    );
}
